import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[2px] text-[0.8rem] font-medium tracking-[0.02em] uppercase transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(245,185,66,0.22)] disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[color:var(--accent-strong)] text-black border border-transparent hover:bg-[color:var(--accent)]",
        secondary:
          "bg-[color:var(--bg-elev-2)] text-[color:var(--text)] border border-[color:var(--hairline)] hover:bg-[rgba(255,255,255,0.04)]",
        ghost:
          "bg-transparent text-[color:var(--muted)] border border-[color:var(--hairline)] hover:text-[color:var(--text)] hover:bg-[color:var(--bg-hover)]",
        outline:
          "border border-[color:var(--border-strong)] bg-transparent text-[color:var(--text)] hover:bg-[color:var(--bg-hover)]",
      },
      size: {
        default: "h-9 px-3.5 py-2",
        sm: "h-8 rounded-[2px] px-3 text-[0.72rem]",
        lg: "h-10 rounded-[2px] px-5",
        icon: "h-8 w-8 rounded-[2px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
